import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import { useEffect, useMemo } from "react";
import { Pressable, View } from "react-native";
import { useTranslation } from "react-i18next";
import { getStatusIconConfig, getDependencyNames } from "../../Utils/plannerStepUtils";

const StepItem = ({
  step,
  isSelected,
  onClick,
  currentTheme,
  isLast = false,
  getAgent,
  isParallel = false,
  allSteps = [],
  isActivelyStreaming = false,
}) => {
  const { t } = useTranslation();
  const statusConfig = getStatusIconConfig(step.status, currentTheme);

  // Get dependency names for display
  const dependencyNames = useMemo(() => {
    return getDependencyNames(step, allSteps);
  }, [step, allSteps]);

  const hasDependencies = step.dependsOn && step.dependsOn.length > 0;

  // Resolve agent ID to agent name. For dynamically-spawned agents the plan
  // uses a "new_agent_*" placeholder id, so prefer the real name resolved
  // from the bucketed message's agentID/agentName before the generic label.
  const getAgentDisplayName = () => {
    const assignedTo = step.assignedTo || step.agentId;
    const primaryMessage = step.messages?.[0];
    const messageAgentName =
      primaryMessage?.agentName ||
      step.messages?.find((m) => m?.agentName)?.agentName ||
      null;
    const messageAgentId = primaryMessage?.agentID || null;
    const resolvedFromMessage =
      messageAgentName ||
      (getAgent && messageAgentId ? getAgent(messageAgentId)?.label : null);

    if (!assignedTo) return resolvedFromMessage || null;

    if (/^new_agent/i.test(assignedTo)) {
      return resolvedFromMessage || t("planner.steps.newAgent");
    }

    // Check if assignedTo is a UUID (agent ID) - try to resolve it
    if (getAgent && typeof getAgent === 'function') {
      const agent = getAgent(assignedTo);
      if (agent?.label) return agent.label;
    }

    // Fall back to assignedTo (which is already the agent name from plan parsing),
    // then to the message-resolved name.
    return assignedTo || resolvedFromMessage;
  };

  const agentDisplayName = getAgentDisplayName();

  // Inject kaleidoscope CSS animation for actively streaming status
  useEffect(() => {
    if (!isActivelyStreaming) return;

    const styleId = "step-item-kaleidoscope-animation";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes kaleidoscope {
          0% {
            border-radius: 20%;
            transform: rotate(0deg) scale(1);
          }
          25% {
            border-radius: 50%;
            transform: rotate(90deg) scale(0.8);
          }
          50% {
            border-radius: 10%;
            transform: rotate(180deg) scale(1.1);
          }
          75% {
            border-radius: 50%;
            transform: rotate(270deg) scale(0.9);
          }
          100% {
            border-radius: 20%;
            transform: rotate(360deg) scale(1);
          }
        }
      `;
      document.head.appendChild(style);
    }
  }, [isActivelyStreaming]);

  return (
    <Pressable
      onPress={onClick}
      style={({ pressed, hovered }) => ({
        flexDirection: "row",
        alignItems: "flex-start",
        paddingVertical: 6,
        paddingHorizontal: 8,
        marginBottom: 2,
        borderRadius: 6,
        backgroundColor: isSelected
          ? currentTheme?.theme?.softBackgroundColor
          : hovered
          ? currentTheme?.theme?.grey
          : "transparent",
        opacity: pressed ? 0.8 : 1,
        borderLeftWidth: isSelected ? 2 : 0,
        borderLeftColor: currentTheme?.theme?.primary,
      })}
    >
      {/* Timeline indicator */}
      <View
        style={{
          width: 24,
          alignItems: "center",
          marginRight: 8,
        }}
      >
        {/* Status icon - only animate when actively streaming via SSE */}
        {isActivelyStreaming ? (
          <View
            style={{
              width: 12,
              height: 12,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div
              style={{
                width: 10,
                height: 10,
                backgroundColor: statusConfig.color,
                animation: "kaleidoscope 2s ease-in-out infinite",
              }}
            />
          </View>
        ) : (
          <View
            style={{
              width: 12,
              height: 12,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <AppIcon
              icon={AppAssets?.icons?.[statusConfig.iconKey] || AppAssets?.icons?.pending}
              size={12}
              color={statusConfig.color}
            />
          </View>
        )}

        {/* Connecting line */}
        {!isLast && (
          <View
            style={{
              width: 1,
              flex: 1,
              minHeight: 16,
              backgroundColor: currentTheme?.theme?.border_color,
              marginTop: 2,
            }}
          />
        )}
      </View>

      {/* Step content */}
      <View style={{ flex: 1 }}>
        {/* Step number with parallel indicator */}
        <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 2 }}>
          <AppText
            style={{
              fontSize: currentTheme?.theme?.extra_small_font_size,
              color: currentTheme?.theme?.darkGrey,
            }}
            preventTranslation
          >
            Step {step.stepIndex + 1}
            {step.isConditional && t("planner.steps.conditional")}
          </AppText>

          {/* Parallel execution badge */}
          {isParallel && (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                backgroundColor: currentTheme?.theme?.primary,
                paddingHorizontal: 6,
                paddingVertical: 2,
                borderRadius: 4,
                marginLeft: 6,
              }}
            >
              <AppIcon
                icon={AppAssets?.icons?.parallel || AppAssets?.icons?.copy}
                size={10}
                color="#FFFFFF"
                wrapperStyle={{ marginRight: 3 }}
              />
              <AppText
                style={{
                  fontSize: 9,
                  color: "#FFFFFF",
                  fontWeight: "600",
                }}
                preventTranslation
              >
                Parallel
              </AppText>
            </View>
          )}

          {/* Authorization required badge */}
          {step.authorisationRequired && (
            <View
              style={{
                backgroundColor: `${currentTheme?.theme?.orange}15`,
                paddingHorizontal: 6,
                paddingVertical: 2,
                borderRadius: 4,
                marginLeft: 6,
              }}
>
              <AppText
                style={{
                  fontSize: 9,
                  color: currentTheme?.theme?.orange,
                  fontWeight: "600",
                }}
                preventTranslation
              >
                {t("planner.steps.authRequired")}
              </AppText>
            </View>
          )}
        </View>

        {/* Task name */}
        <AppText
          numberOfLines={isSelected ? undefined : 2}
          style={{
            fontSize: currentTheme?.theme?.small_font_size,
            color: currentTheme?.theme?.font_color,
            fontWeight: isSelected ? "600" : "normal",
          }}
          preventTranslation
        >
          {step.taskName}
        </AppText>

        {/* Dependencies indicator (when selected) */}
        {isSelected && hasDependencies && (
          <View
            style={{
              marginTop: 4,
              padding: 6,
              backgroundColor: `${currentTheme?.theme?.grey}50`,
              borderRadius: 4,
            }}
          >
            <AppText
              style={{
                fontSize: currentTheme?.theme?.extra_small_font_size,
                color: currentTheme?.theme?.darkGrey,
                fontWeight: "600",
                marginBottom: 2,
              }}
              preventTranslation
            >
              {t("planner.steps.dependsOn")}
            </AppText>
            {dependencyNames.map((depName, idx) => (
              <AppText
                key={idx}
                style={{
                  fontSize: currentTheme?.theme?.extra_small_font_size,
                  color: currentTheme?.theme?.darkGrey,
                }}
                numberOfLines={1}
                preventTranslation
              >
                • {depName}
              </AppText>
            ))}
          </View>
        )}

        {/* Error message if failed */}
        {step.isFailed && step.error && (
          <AppText
            numberOfLines={2}
            style={{
              fontSize: currentTheme?.theme?.extra_small_font_size,
              color: currentTheme?.theme?.red,
              marginTop: 2,
            }}
            preventTranslation
          >
            {step.error}
          </AppText>
        )}

        {/* Agent name */}
        {agentDisplayName && (
          <AppText
            style={{
              fontSize: currentTheme?.theme?.extra_small_font_size,
              color: currentTheme?.theme?.darkGrey,
              marginTop: 2,
            }}
            preventTranslation
          >
            {agentDisplayName}
          </AppText>
        )}
      </View>

      {/* Expand indicator */}
      <AppIcon
        icon={AppAssets?.icons?.chevronRight || AppAssets?.icons?.goTo}
        size={10}
        color={currentTheme?.theme?.darkGrey}
        wrapperStyle={{
          transform: [{ rotate: isSelected ? "90deg" : "0deg" }],
          alignSelf: "center",
        }}
      />
    </Pressable>
  );
};

export default StepItem;
